import React, { memo, useState, useMemo, useEffect } from 'react'
import { Card, Modal, Col, Space, Button, Tooltip, Row } from 'antd'
import { ContainerCard } from '../../../../reusable-components/AgiliteCards'
import PatientDependants from '../PatientDependants'
import BeginExaminationModal from '../BeginExaminationModal.js'
import EmployeeBookingsView from '../../../../Bookings/components/Booking View Components/EmployeeBookingsListview/index'
import ScriptsWrapper from '../../../../Scripting/components/scripting-wrapper'
// NOTE: PatientAnalytics has been removed - analytics now handled by Assessment system in examinations
import useEditPatient from '../../hooks/useEditPatient'
import FeatureButtonsRow from '../common/FeatureButtonsRow'
import PatientProfileTab from './PatientProfileTab'
import ModalContent from './ModalContent'
import CustomRow from '../../../../reusable-components/CustomRow'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faUser,
  faUsers,
  faFilePrescription,
  faFileAlt,
  faChartLine,
  faCalendarCheck
} from '@fortawesome/free-solid-svg-icons'
import DocumentsWrapper from '../../../../Examination/components/Documents/DocumentsWrapper'
import { isMedicalProfessional } from '../../../../lib/profile-utils'
import CustomTabs from './CustomTabs'
import { useDispatch } from 'react-redux'
import coreReducer from '../../../../../core/utils/reducer'
import CoreEnums from '../../../../../core/utils/enums'
import store from '../../../../../store'
import PatientFileViewer from '../../../../Patient/Desktop/components/PatientFileViewer'

/**
 * EditPatientProfile component for editing patient information
 * @param {Object} props
 * @param {string} props.dataId - The ID of the patient data
 * @param {Function} props.refreshView - Function to refresh the view
 * @param {Function} props.handleRootTabKey - Function to handle the root tab key
 * @param {boolean} props.isExamination - Whether this is being used in an examination context
 * @param {boolean} props.isDependant - Whether this profile is for a dependant
 * @param {string} props.parentId - The ID of the parent patient if this is a dependant
 */
const EditPatientProfile = memo(
  ({
    dataId,
    refreshView,
    handleRootTabKey,
    isExamination = false,
    isDependant = false,
    parentId,
    isNew,
    parentTabKey,
    initialTab = null
  }) => {
    // Use the custom hook to get all the state and functions
    const {
      beginAdhocScreening,
      closeTab,
      handleGetMedicalAidPlanOptions,
      handleGetMedicalAidPlans,
      handleLoginPhoneCode,
      handleModalReset,
      handleSubmit,
      setAssessmentModalOpen,
      setClinicalNotesModalOpen,
      setConfirmationCode,
      setConfirmationOpen,
      setCountry,
      setExaminationModalOpen,
      setFollowUpModalOpen,
      setMedicalHistoryModalOpen,
      setPlanOptionData,
      setPlansData,
      setReferralModalOpen,
      setResidentVisitor,
      setSchemeData,
      setSickNoteModalOpen,
      setSpecialFormState,
      assessmentModalOpen,
      authState,
      clinicalNotesModalOpen,
      confirmationCode,
      confirmationOpen,
      countries,
      countriesLoading,
      countryObject,
      dataIndex,
      debouncedSchemeFilter,
      examinationModalOpen,
      followUpModalOpen,
      form,
      loading,
      medicalHistoryModalOpen,
      planOptionData,
      planOptionsLoading,
      plansData,
      plansLoading,
      referralModalOpen,
      residentVisitor,
      schemeData,
      schemeLoading,
      sickNoteModalOpen,
      specialFormState,
      submitDisabled,
      token,
      userData
    } = useEditPatient({ dataId, refreshView, isExamination, handleRootTabKey })

    // Add state for document updates
    const [bookingData, setBookingData] = useState({ userRef: dataId, patientRecord: userData })
    // Add state for active tab - always default to 'patientProfile' for patient file view
    const [activeTabKey, setActiveTabKey] = useState(initialTab || 'patientProfile')
    // Get dispatch function
    const dispatch = useDispatch()
    // State to track if this is actually a dependant profile
    const [isActuallyDependant, setIsActuallyDependant] = useState(isDependant)

    // Detect if this is a dependant profile based on the userData
    useEffect(() => {
      if (userData) {
        // If the user has a mainMemberId, they are a dependant
        const isDependantProfile = !!userData.mainMemberId

        // Log a warning if this is a dependant but not explicitly marked as such
        if (isDependantProfile && !isDependant) {
          console.warn(
            'Dependant profile detected but not explicitly marked as such. ' +
              'Please pass isDependant=true when opening dependant profiles. ' +
              `User ID: ${dataId}, Main Member ID: ${userData.mainMemberId}`
          )
        }

        // Check if current user is a patient viewing their dependant
        // In this case, they should see all tabs, not the limited dependant view
        const currentUserIsPatient = authState.agiliteUser?.extraData?.role?.type === 'patient'
        const isViewingOwnDependant = currentUserIsPatient && (isDependant || isDependantProfile)

        // Only set as actually dependant if it's not a patient viewing their own dependant
        setIsActuallyDependant((isDependant || isDependantProfile) && !isViewingOwnDependant)

        // If this is a dependant profile, make sure we're on the patient profile tab
        if ((isDependant || isDependantProfile) && !isViewingOwnDependant) {
          setActiveTabKey('patientProfile')
        }
      }
    }, [userData, isDependant, dataId, authState.agiliteUser])

    const buttonStyle = {
      height: '40px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '0 16px',
      fontWeight: 500,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease'
    }

    // Check if the current user is a medical professional
    const userIsMedicalProfessional = useMemo(() => {
      return isMedicalProfessional()
    }, [authState.agiliteUser.extraData.role.type])

    // Function to render tab with icon using BEM-style class names
    const renderTabWithIcon = (title, icon, iconType) => (
      <span>
        <FontAwesomeIcon
          icon={icon}
          className={`patient-profile-tabs__icon--${iconType}`}
          style={{ marginRight: '8px' }}
        />
        {title}
      </span>
    )

    // Handle back button click
    const handleBackClick = () => {
      console.log('=== BACK BUTTON CLICKED DEBUG ===')
      console.log('isExamination:', isExamination)
      console.log('isActuallyDependant:', isActuallyDependant)
      console.log('parentId:', parentId)
      console.log('userData:', userData)
      console.log('userData.mainMemberId:', userData?.mainMemberId)

      if (isExamination) {
        refreshView('cancel')
      } else if (isDependant || userData?.mainMemberId) {
        // Check if current user is a patient viewing their dependant
        const currentUserIsPatient = authState.agiliteUser?.extraData?.role?.type === 'patient'
        const isPatientViewingDependant = currentUserIsPatient && (isDependant || userData?.mainMemberId)

        if (isPatientViewingDependant) {
          // For patients viewing their dependants, navigate back to patient file viewer
          console.log('Patient viewing dependant - navigating back to patient file viewer')

          // First close the current dependant tab
          closeTab()

          // Check if we have a parentTabKey that might be the patient file viewer
          if (parentTabKey && parentTabKey.includes(CoreEnums.tabKeys.MY_PATIENT_FILE)) {
            console.log('Using parentTabKey to navigate back:', parentTabKey)

            // Check if the parent tab still exists in the tabs array
            const coreState = store.getState().core
            const tabExists = coreState.tabNavigation.tabs.some((tab) => tab.key === parentTabKey)

            if (tabExists) {
              console.log('Parent tab exists, navigating to it')
              dispatch(
                coreReducer.actions.changeTab({
                  activeKey: parentTabKey
                })
              )
            } else {
              console.log('Parent tab does not exist, recreating patient file viewer')
              // Recreate the patient file viewer tab
              dispatch(
                coreReducer.actions.addTab({
                  key: parentTabKey,
                  closable: true,
                  label: 'My Patient File',
                  children: React.createElement(PatientFileViewer)
                })
              )
            }
          } else {
            // Fallback: Go back to the main patient file landing
            console.log('No valid parent tab, navigating to patient file landing')
            dispatch(
              coreReducer.actions.changeTab({
                activeKey: CoreEnums.tabKeys.MY_PATIENT_FILE
              })
            )
          }
        } else {
          // For non-patients, use the existing flow
          closeTab()

          // For medical professionals viewing dependants, use existing logic
          // Option 1: If we have a direct parent tab key, use it
          if (parentTabKey) {
            console.log('Using direct parent tab key:', parentTabKey)
            dispatch(
              coreReducer.actions.menuItemClick({
                key: parentTabKey
              })
            )
          }
          // Option 2: If we have a valid parentId, try to navigate to parent
          else if (parentId && handleRootTabKey) {
            const constructedParentTabKey = `${handleRootTabKey()}_${parentId}`
            console.log('Attempting to navigate to parent tab:', constructedParentTabKey)

            dispatch(
              coreReducer.actions.menuItemClick({
                key: constructedParentTabKey
              })
            )
          }
          // Option 3: If no parentId but we're in a patient context, go to patients list
          else if (handleRootTabKey) {
            console.log('No parent ID available, navigating to patients list')
            dispatch(
              coreReducer.actions.menuItemClick({
                key: handleRootTabKey()
              })
            )
          }
          // Option 4: Final fallback - always go to patients list
          else {
            console.log('Using final fallback - navigating to patients list')
            dispatch(
              coreReducer.actions.menuItemClick({
                key: CoreEnums.tabKeys.PATIENTS
              })
            )
          }
        }
      } else {
        console.log('Closing tab (not a dependant)')
        closeTab()
      }
    }

    // Render the tab bar extra content (feature buttons)
    const renderTabBarExtraContent = () => {
      if (!userIsMedicalProfessional || isExamination) {
        return null
      }

      return (
        <FeatureButtonsRow
          setExaminationModalOpen={setExaminationModalOpen}
          setClinicalNotesModalOpen={setClinicalNotesModalOpen}
          setMedicalHistoryModalOpen={() => setMedicalHistoryModalOpen(true)}
          handleBack={handleBackClick}
          isExamination={!!isExamination}
          hideBackButton={true}
        />
      )
    }

    // Custom styles for the tabs
    const tabsStyle = {
      '.ant-tabs-tab': {
        height: '48px', // Increase tab height
        lineHeight: '48px', // Ensure text is vertically centered
        padding: '0 16px' // Adjust padding for better spacing
      },
      '.ant-tabs-ink-bar': {
        height: '3px', // Make the indicator line thicker
        background: '#1890ff', // Ensure it's the primary blue color
        bottom: '0', // Position at the bottom
        zIndex: 1 // Ensure it's above the border
      }
    }

    // Get the tabs to display based on whether this is a dependant profile
    const getTabs = () => {
      // Common tabs for both main patients and dependants
      const commonTabs = (
        <>
          {/* Patient Profile Tab - Available to all roles */}
          <CustomTabs.TabPane key='patientProfile' tab={renderTabWithIcon('Patient Profile', faUser, 'profile')}>
            <PatientProfileTab
              userData={userData}
              form={form}
              loading={loading}
              token={token}
              isExamination={isExamination}
              refreshView={refreshView}
              closeTab={closeTab}
              countries={countries}
              countriesLoading={countriesLoading}
              residentVisitor={residentVisitor}
              setResidentVisitor={setResidentVisitor}
              setCountry={setCountry}
              countryObject={countryObject}
              schemeLoading={schemeLoading}
              schemeData={schemeData}
              debouncedSchemeFilter={debouncedSchemeFilter}
              plansLoading={plansLoading}
              plansData={plansData}
              handleGetMedicalAidPlans={handleGetMedicalAidPlans}
              planOptionsLoading={planOptionsLoading}
              planOptionData={planOptionData}
              handleGetMedicalAidPlanOptions={handleGetMedicalAidPlanOptions}
              specialFormState={specialFormState}
              setSpecialFormState={setSpecialFormState}
              setSchemeData={setSchemeData}
              setPlansData={setPlansData}
              setPlanOptionData={setPlanOptionData}
              handleSubmit={handleSubmit}
            />
          </CustomTabs.TabPane>
        </>
      )

      // If this is a dependant profile, only show a limited set of tabs
      if (isActuallyDependant) {
        return (
          <>
            {commonTabs}

            {/* Appointments Tab - Available for dependants */}
            <CustomTabs.TabPane tab={renderTabWithIcon('Appointments', faCalendarCheck, 'bookings')} key='bookings'>
              <EmployeeBookingsView
                userRef={dataId}
                viewingPatientProfile={true}
                readOnly={!userIsMedicalProfessional}
              />
            </CustomTabs.TabPane>

            {/* Medical Professional Only Tabs */}
            {userIsMedicalProfessional && (
              <>
                <CustomTabs.TabPane tab={renderTabWithIcon('Scripts', faFilePrescription, 'scripting')} key='scripts'>
                  <ScriptsWrapper userRef={dataId} patientId={dataId} />
                </CustomTabs.TabPane>
                <CustomTabs.TabPane tab={renderTabWithIcon('Documents', faFileAlt, 'documents')} key='documents'>
                  <DocumentsWrapper
                    bookingData={bookingData}
                    userRef={dataId}
                    setBookingData={setBookingData}
                    noBooking={true}
                  />
                </CustomTabs.TabPane>
                <CustomTabs.TabPane tab={renderTabWithIcon('Analytics', faChartLine, 'analytics')} key='analytics'>
                  <Card style={{ textAlign: 'center', padding: '40px' }}>
                    <div style={{ fontSize: '16px', marginBottom: '16px' }}>
                      📊 Analytics functionality has been modernized
                    </div>
                    <div style={{ color: '#666', marginBottom: '24px' }}>
                      Patient analytics and health data visualization are now available through:
                      <br />• <strong>Assessment system</strong> during patient examinations
                      <br />• <strong>Health Stats</strong> in the mobile app for patients
                    </div>
                    <Button
                      type='primary'
                      onClick={() => setExaminationModalOpen(true)}
                      style={{ marginRight: '12px' }}
                    >
                      Begin Examination
                    </Button>
                    <Button onClick={() => setAssessmentModalOpen(true)}>Open Assessment</Button>
                  </Card>
                </CustomTabs.TabPane>
              </>
            )}
          </>
        )
      }

      // For main patient profiles, show all tabs
      return (
        <>
          {commonTabs}

          {/* Other Tabs - Only shown when not in examination mode */}
          {!isExamination && (
            <>
              {/* Dependants Tab - Available to all roles */}
              <CustomTabs.TabPane key='dependants' tab={renderTabWithIcon('Dependants', faUsers, 'dependants')}>
                <PatientDependants
                  mainMemeberDataIndex={dataIndex}
                  userRef={userData._id}
                  mainPatientInfo={userData}
                  parentTabKey={
                    authState.agiliteUser?.extraData?.role?.type === 'patient'
                      ? `${CoreEnums.tabKeys.MY_PATIENT_FILE}_viewer`
                      : `${handleRootTabKey()}_${dataId}`
                  } // Pass the correct parent tab key based on user type
                />
              </CustomTabs.TabPane>

              {/* Scripts, Documents, Analytics Tabs - Available to Patients and Medical Professionals Only */}
              {(userIsMedicalProfessional || authState.agiliteUser?.extraData?.role?.type === 'patient') && (
                <>
                  <CustomTabs.TabPane tab={renderTabWithIcon('Scripts', faFilePrescription, 'scripting')} key='scripts'>
                    <ScriptsWrapper patientId={dataId} />
                  </CustomTabs.TabPane>

                  <CustomTabs.TabPane tab={renderTabWithIcon('Documents', faFileAlt, 'documents')} key='documents'>
                    <DocumentsWrapper
                      bookingData={bookingData}
                      userRef={dataId}
                      setBookingData={setBookingData}
                      noBooking={true}
                    />
                  </CustomTabs.TabPane>

                  <CustomTabs.TabPane tab={renderTabWithIcon('Analytics', faChartLine, 'analytics')} key='analytics'>
                    <Card style={{ textAlign: 'center', padding: '40px' }}>
                      <div style={{ fontSize: '16px', marginBottom: '16px' }}>
                        📊 Analytics functionality has been modernized
                      </div>
                      <div style={{ color: '#666', marginBottom: '24px' }}>
                        Patient analytics and health data visualization are now available through:
                        <br />• <strong>Assessment system</strong> during patient examinations
                        <br />• <strong>Health Stats</strong> in the mobile app for patients
                      </div>
                      {/* Medical Professional Only Buttons */}
                      {userIsMedicalProfessional && (
                        <>
                          <Button
                            type='primary'
                            onClick={() => setExaminationModalOpen(true)}
                            style={{ marginRight: '12px' }}
                          >
                            Begin Examination
                          </Button>
                          <Button onClick={() => setAssessmentModalOpen(true)}>Open Assessment</Button>
                        </>
                      )}
                    </Card>
                  </CustomTabs.TabPane>
                </>
              )}

              {/* Appointments Tab - Available to all roles but with different functionality */}
              <CustomTabs.TabPane tab={renderTabWithIcon('Appointments', faCalendarCheck, 'bookings')} key='bookings'>
                <EmployeeBookingsView
                  userRef={dataId}
                  viewingPatientProfile={true}
                  readOnly={!userIsMedicalProfessional} // Set to read-only for non-medical professionals
                />
              </CustomTabs.TabPane>
            </>
          )}
        </>
      )
    }

    return (
      <ContainerCard
        backFunction={
          isExamination
            ? null
            : () => {
                closeTab()
              }
        }
        title='Edit Patient Profile'
        style={{ paddingBottom: 12 }}
        headStyle={isExamination ? { display: 'none' } : {}}
      >
        {/* Main Tabs with Feature Buttons on the right */}
        <CustomTabs
          activeKey={activeTabKey}
          onChange={setActiveTabKey}
          tabBarExtraContent={renderTabBarExtraContent()}
          style={tabsStyle}
        >
          {/* Back Button Tab - Only shown when not in examination mode */}
          {!isExamination && (
            <CustomTabs.TabPane
              key='back'
              tab={
                <Button
                  style={{
                    ...buttonStyle,
                    background: '#f5f5f5',
                    borderColor: '#d9d9d9',
                    color: '#595959',
                    height: '32px',
                    padding: '0 8px'
                  }}
                  icon={<FontAwesomeIcon icon={faArrowLeft} style={{ color: '#595959' }} />}
                  onClick={handleBackClick}
                >
                  Back
                </Button>
              }
              disabled
            />
          )}

          {getTabs()}
        </CustomTabs>

        {/* Recaptcha Container */}
        <div id='recaptcha-container'></div>

        {/* Begin Examination Modal - Only for medical professionals */}
        {userIsMedicalProfessional && (
          <BeginExaminationModal
            examinationModalOpen={examinationModalOpen}
            setExaminationModalOpen={setExaminationModalOpen}
            beginAdhocExamination={beginAdhocScreening}
          />
        )}

        {/* Follow-up Modal - Only for medical professionals */}
        {userIsMedicalProfessional && (
          <Modal
            footer={false}
            maskClosable={false}
            closable={false}
            width={500}
            open={followUpModalOpen}
            okButtonProps={{ style: { display: 'none' } }}
            cancelButtonProps={{ style: { display: 'none' } }}
            onCancel={() => setFollowUpModalOpen(false)}
            destroyOnClose
          >
            <ModalContent
              followUpModalOpen={followUpModalOpen}
              setFollowUpModalOpen={setFollowUpModalOpen}
              userData={userData}
              token={token}
            />
          </Modal>
        )}

        {/* Sick Note Modal - Only for medical professionals */}
        {userIsMedicalProfessional && (
          <Modal
            footer={false}
            maskClosable={false}
            closable={false}
            width={950}
            open={sickNoteModalOpen}
            okButtonProps={{ style: { display: 'none' } }}
            cancelButtonProps={{ style: { display: 'none' } }}
            onCancel={() => setSickNoteModalOpen(false)}
            destroyOnClose
          >
            <ModalContent
              sickNoteModalOpen={sickNoteModalOpen}
              setSickNoteModalOpen={setSickNoteModalOpen}
              userData={userData}
              token={token}
            />
          </Modal>
        )}

        {/* Referral Modal - Only for medical professionals */}
        {userIsMedicalProfessional && (
          <Modal
            footer={false}
            maskClosable={false}
            closable={false}
            width={950}
            open={referralModalOpen}
            okButtonProps={{ style: { display: 'none' } }}
            cancelButtonProps={{ style: { display: 'none' } }}
            onCancel={() => setReferralModalOpen(false)}
            destroyOnClose
          >
            <ModalContent
              referralModalOpen={referralModalOpen}
              setReferralModalOpen={setReferralModalOpen}
              userData={userData}
              token={token}
            />
          </Modal>
        )}

        {/* Assessment Modal - Only for medical professionals */}
        {userIsMedicalProfessional && (
          <Modal
            footer={false}
            maskClosable={false}
            closable={false}
            width={1920}
            open={assessmentModalOpen}
            okButtonProps={{ style: { display: 'none' } }}
            cancelButtonProps={{ style: { display: 'none' } }}
            onCancel={() => setAssessmentModalOpen(false)}
            destroyOnClose
          >
            <ModalContent
              assessmentModalOpen={assessmentModalOpen}
              setAssessmentModalOpen={setAssessmentModalOpen}
              userData={userData}
              token={token}
            />
          </Modal>
        )}

        {/* Clinical Notes Modal - Only for medical professionals */}
        {userIsMedicalProfessional && (
          <Modal
            footer={false}
            maskClosable={false}
            closable={false}
            width={950}
            open={clinicalNotesModalOpen}
            okButtonProps={{ style: { display: 'none' } }}
            cancelButtonProps={{ style: { display: 'none' } }}
            onCancel={() => setClinicalNotesModalOpen(false)}
            destroyOnClose
          >
            <ModalContent
              clinicalNotesModalOpen={clinicalNotesModalOpen}
              setClinicalNotesModalOpen={setClinicalNotesModalOpen}
              userData={userData}
              token={token}
            />
          </Modal>
        )}

        {/* Medical History Modal - Only for medical professionals */}
        {userIsMedicalProfessional && (
          <Modal
            footer={false}
            maskClosable={false}
            closable={false}
            width={950}
            open={medicalHistoryModalOpen}
            okButtonProps={{ style: { display: 'none' } }}
            cancelButtonProps={{ style: { display: 'none' } }}
            onCancel={() => setMedicalHistoryModalOpen(false)}
            destroyOnClose
          >
            <ModalContent
              medicalHistoryModalOpen={medicalHistoryModalOpen}
              setMedicalHistoryModalOpen={setMedicalHistoryModalOpen}
              userData={userData}
              token={token}
            />
          </Modal>
        )}

        {/* One-time Pin Modal */}
        <Modal title='One-time Pin' footer={null} open={confirmationOpen} onCancel={() => setConfirmationOpen(false)}>
          <ModalContent
            confirmationCode={confirmationCode}
            setConfirmationCode={setConfirmationCode}
            submitDisabled={submitDisabled}
            form={form}
            handleSubmit={handleSubmit}
            handleLoginPhoneCode={handleLoginPhoneCode}
            token={token}
            isConfirmationModal={true}
          />
        </Modal>
      </ContainerCard>
    )
  }
)

EditPatientProfile.displayName = 'EditPatientProfile'

export default EditPatientProfile
